import { Component, Element, Fragment, h, Listen, Prop } from "@stencil/core";
import { navContentStore } from "@phn/state/nav-content-store";
import {
  getBackButtonText,
  getHeadlineText,
} from "../../services/menu-items-service";
import abTestingService from "@phn/services/ab-testing/ab-testing-constructor-service";
import { navStateStore } from "@phn/state/nav-state-store";
import analyticsManager from "../../services/analytics/analytics-manager";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import {
  ClickElementTypes,
  DisplayElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import {
  findTopLevelRoutingKey,
  RoutingKeys,
} from "@phn/entities/routing-key";
import { getAllFocusableElements } from "./helper";
import { MainDrawerContent } from "./main-drawer-content";
import { SideDrawerContent } from "./side-drawer-content";
import * as routing from "../../services/routing-manager";
import {
  getAnimatedRoute,
  getCurrentRoute,
} from "../../services/routing-manager";
import { routerStore } from "@phn/state/router-store";
import { handleAnimationRouting } from "./animations/animations-controller";
import { registerDoubleDrawerAnimationsSequences } from "./animations/animations-register";
import {
  focusOnElement,
  getHTMLElement,
  isString,
} from "@phn/utility/helper";

@Component({
  tag: "phn-nd-double-drawer",
  styleUrl: "double-drawer.scss",
  scoped: true,
})
export class DoubleDrawer {
  @Prop() locale!: string;

  @Prop() receivedContent!: boolean;

  @Element() el!: HTMLPhnNdDoubleDrawerElement;

  @Listen("keydown", { passive: false })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key !== "Tab") {
      return;
    }

    let activeRoutingKey = routing.getCurrentRoute();
    if (activeRoutingKey.startsWith(RoutingKeys.DEALER_SEARCH)) {
      [activeRoutingKey] = activeRoutingKey.split("/");
    }

    const eventSourceElement = getHTMLElement(e.target);
    if (eventSourceElement === null) {
      return;
    }

    const focusableSideDrawerElements = getAllFocusableElements(
      this.el.querySelector("#side-drawer"),
    );
    let focusableMainDrawerElements = getAllFocusableElements(
      this.el.querySelector("#main-drawer"),
    );

    if (
      focusableSideDrawerElements.length === 0 ||
      focusableMainDrawerElements.length === 0
    ) {
      return;
    }

    const highlightedSideDrawerElementIndex =
      focusableSideDrawerElements.findIndex(
        (el) => el.getAttribute("data-id") === activeRoutingKey,
      );
    const highlightedSideDrawerElement =
      focusableSideDrawerElements[highlightedSideDrawerElementIndex];

    if (activeRoutingKey === RoutingKeys.DEALER_SEARCH) {
      focusableMainDrawerElements = focusableMainDrawerElements.filter(
        (element) => element.nodeName !== "BUTTON" && element.nodeName !== "A",
      );
    }

    const lastSideDrawerElement =
      focusableSideDrawerElements[focusableSideDrawerElements.length - 1];
    const lastMainDrawerElement =
      focusableMainDrawerElements[focusableMainDrawerElements.length - 1];

    const closeButton = this.el.querySelector(".close-button");

    if (!e.shiftKey) {
      if (eventSourceElement === highlightedSideDrawerElement) {
        e.preventDefault();
        focusOnElement(focusableMainDrawerElements[0]);
        return;
      }

      if (eventSourceElement === lastSideDrawerElement) {
        e.preventDefault();
        if (closeButton !== null) {
          focusOnElement(closeButton);
        }
      }

      if (eventSourceElement === lastMainDrawerElement) {
        e.preventDefault();

        const nextSideDrawerElementIndex =
          highlightedSideDrawerElementIndex + 1 >=
          focusableSideDrawerElements.length
            ? 0
            : highlightedSideDrawerElementIndex + 1;

        const nextSideDrawerElement = getHTMLElement(
          focusableSideDrawerElements[nextSideDrawerElementIndex],
        );

        focusOnElement(nextSideDrawerElement);
      }

      if (eventSourceElement === closeButton) {
        e.preventDefault();
        focusOnElement(focusableSideDrawerElements[0]);
      }
    } else if (e.shiftKey) {
      if (eventSourceElement === focusableMainDrawerElements[0]) {
        e.preventDefault();
        if (highlightedSideDrawerElement !== null) {
          focusOnElement(highlightedSideDrawerElement);
        }
      }

      if (eventSourceElement === closeButton) {
        e.preventDefault();
        focusOnElement(lastSideDrawerElement);
      }
    }
  }

  private sendDoubleDrawerLoadDataEvent() {
    analyticsManager.setDrawerType(DisplayElementTypes.DoubleDrawer);
    const activeRoutingKey: string = getAnimatedRoute() || "";

    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DRAWER_LOAD,
      {
        componentDisplay: {
          displayElementType: analyticsManager.calculateDisplayElementType(
            activeRoutingKey,
            ElementTypes.doubleDrawer,
          ),
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementName: "Double Drawer",
        },
      },
    );
  }

  private sendDataEventOfCloseButtonClick() {
    const activeRoutingKey: string = getAnimatedRoute() || "";
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.MENU_CLOSE_CLICK,
      {
        componentClick: {
          clickElementId: "mainmenu.drawer.close",
          clickElementName: "X",
          clickElementType: ClickElementTypes.Button,
          targetElement: "level-0",
        },
        componentDisplay: {
          displayElementType: analyticsManager.calculateDisplayElementType(
            activeRoutingKey,
            ElementTypes.closeButton,
          ),
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementName: "Double Drawer",
        },
      },
    );
  }

  async componentWillLoad(): Promise<void> {
    registerDoubleDrawerAnimationsSequences();
    this.sendDoubleDrawerLoadDataEvent();
    await abTestingService().init(navStateStore.state.app);
  }

  render(): HTMLPhnNdDoubleDrawerElement {
    const { route } = handleAnimationRouting();

    const previousRoutingKey =
      routerStore.state.routingKeyHistory[
        routerStore.state.routingKeyPointer - 1
      ];
    const backButtonText = getBackButtonText(
      navContentStore.state,
      previousRoutingKey,
    );
    const topLevelRoutingKey = findTopLevelRoutingKey(getCurrentRoute());
    const wasPreviouslyOnDealerSearch =
      findTopLevelRoutingKey(previousRoutingKey) === RoutingKeys.DEALER_SEARCH;

    const displayHeadlineContent =
      topLevelRoutingKey !== undefined
        ? getHeadlineText(
            navContentStore.state,
            getCurrentRoute(),
            topLevelRoutingKey,
          )
        : "";
    const modelType = displayHeadlineContent?.includes("mainmenu.")
      ? displayHeadlineContent.split("mainmenu.")[1]
      : "";

    return (
      <div
        role="dialog"
        aria-modal="true"
        class="phn-double-drawer"
        aria-label="navigation modal dialog"
      >
        <phn-p-button-pure
          class="close-button"
          theme="dark"
          hide-label="true"
          style={{ padding: "0.75rem" }}
          icon={"close"}
          size={"medium"}
          aria="{ 'aria-label': 'close modal dialog' }"
          data-test-id="close-button"
          onClick={() => {
            this.sendDataEventOfCloseButtonClick();
            routing.reset();
          }}
        />
        {isString(route) ? (
          <Fragment>
            <div class="drawer" id="side-drawer">
              <SideDrawerContent
                activeRoutingKey={route}
                locale={this.locale}
                backButtonText={
                  wasPreviouslyOnDealerSearch ? "" : backButtonText
                }
                onClickBack={routing.back}
              />
            </div>

            <div class="drawer main-drawer" id="main-drawer">
              <MainDrawerContent
                activeRoutingKey={route}
                locale={this.locale}
                backButtonText={""}
                receivedContent={this.receivedContent}
                onClickBack={routing.back}
                modelType={modelType}
              />
            </div>
          </Fragment>
        ) : null}
      </div>
    );
  }
}
